
import {defineComponent} from 'vue';
import debounce from '../../../../utils/debounce';
import eventsMixin from '../Select/events-mixin';
import navigationMixin from '../Select/navigation-mixin';
import {TOP, BOTTOM, Option, Position, DROPDOWN_POSITIONS} from '../types';
import AutocompleteTextInput from '@ohrm/oxd/core/components/Input/Autocomplete/AutocompleteTextInput.vue';
import AutocompleteDropdown from '@ohrm/oxd/core/components/Input/Autocomplete/AutocompleteDropdown.vue';
import AutocompleteOption from '@ohrm/oxd/core/components/Input/Autocomplete/AutocompleteOption.vue';
import AutocompleteChips from '@ohrm/oxd/core/components/Input/Autocomplete/AutocompleteChips.vue';
import usei18n from '../../../../composables/usei18n';

export default defineComponent({
  name: 'oxd-autocomplete-input',
  inheritAttrs: false,

  components: {
    'oxd-autocomplete-text-input': AutocompleteTextInput,
    'oxd-autocomplete-dropdown': AutocompleteDropdown,
    'oxd-autocomplete-option': AutocompleteOption,
    'oxd-autocomplete-chips': AutocompleteChips,
  },

  mixins: [navigationMixin, eventsMixin],

  emits: [
    'update:modelValue',
    'dropdown:clear',
    'dropdown:opened',
    'dropdown:closed',
    'dropdown:blur',
  ],

  props: {
    modelValue: {},
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    clear: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: null,
    },
    createOptions: {
      type: Function,
      required: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    dropdownPosition: {
      type: String,
      default: BOTTOM,
      validator: function(value: Position) {
        return DROPDOWN_POSITIONS.indexOf(value) !== -1;
      },
    },
  },

  setup() {
    return {
      ...usei18n(),
    };
  },

  data() {
    return {
      focused: false,
      loading: false,
      dropdownOpen: false,
      searchTerm: null,
      options: [],
    };
  },

  computed: {
    computedOptions(): Option[] {
      return this.options.map((option: Option) => {
        let _selected = false;
        if (Array.isArray(this.modelValue)) {
          _selected = this.modelValue.findIndex(o => o.id === option.id) > -1;
        } else if (this.modelValue?.id === option.id) {
          _selected = true;
        }
        return {...option, _selected};
      });
    },
    dropdownClasses(): object {
      return {
        '--positon-bottom': this.dropdownPosition === BOTTOM,
        '--positon-top': this.dropdownPosition === TOP,
      };
    },
    optionClasses(): object[] {
      return this.computedOptions.map((option: Option, index: number) => {
        return {
          '--disabled': option._disabled,
          '--selected': option._selected,
          '--focused': index === this.pointer,
        };
      });
    },
    highlightedOptions(): string[] {
      const filter = new RegExp(this.searchTerm, 'i');
      return this.computedOptions.map((option: Option) => {
        return option.label.replace(filter, match => {
          return `<b>${match}</b>`;
        });
      });
    },
    selectedItem(): string {
      if (!this.multiple && this.modelValue?.label) {
        return this.modelValue.label;
      }
      return null;
    },
    inputValue(): string {
      if (this.computedOptions[this.pointer]?.label) {
        return this.computedOptions[this.pointer].label;
      } else if (this.searchTerm !== null) {
        return this.searchTerm;
      } else if (typeof this.modelValue === 'string') {
        return this.modelValue;
      } else if (!this.dropdownOpen) {
        return this.selectedItem;
      }
      return '';
    },
    showClear(): boolean {
      return (
        !this.disabled &&
        !this.readonly &&
        this.clear &&
        this.selectedItem !== null
      );
    },
    placeholderText(): string {
      return (
        this.placeholder ??
        this.t('general.type_for_hints', 'Type for hints...')
      );
    },
  },

  methods: {
    onSearch($event: Event) {
      const searchTerm = ($event.target as HTMLInputElement).value;
      if (searchTerm) {
        this.loading = true;
        this.dropdownOpen = true;
        this.search(this, searchTerm);
      } else {
        this.loading = false;
        this.dropdownOpen = false;
      }
      this.searchTerm = searchTerm;
    },
    onSelect(option: Option) {
      this.pointer = -1;
      this.searchTerm = null;
      this.dropdownOpen = false;
      if (this.multiple) {
        const selected = Array.isArray(this.modelValue) ? this.modelValue : [];
        this.$emit('update:modelValue', [...selected, option]);
      } else {
        this.$emit('update:modelValue', option);
      }
    },
    search: debounce((vm, searchTerm: string) => {
      new Promise(resolve => {
        if (vm.createOptions) {
          resolve(vm.createOptions(searchTerm));
        } else {
          throw new Error('createOptions not defined');
        }
      })
        .then(resolved => {
          if (resolved && Array.isArray(resolved)) {
            if (resolved.length > 0) {
              vm.options = resolved.slice(0, 5);
            } else {
              vm.options = [];
            }
          } else {
            throw new Error('options returned are not array');
          }
        })
        .finally(() => {
          vm.loading = false;
        });
    }, 800),
    onBlur() {
      this.pointer = -1;
      this.dropdownOpen = false;
      this.$emit('dropdown:blur');
      if (
        this.searchTerm !== null &&
        (this.multiple === false || !Array.isArray(this.modelValue))
      ) {
        this.$emit('update:modelValue', this.searchTerm || null);
      }
    },
  },

  watch: {
    modelValue(value) {
      if (value === null || (Array.isArray(value) && value.length === 0)) {
        this.searchTerm = null;
      }
    },
  },
});
